import { Ref, useMemo } from 'react'

import { isFunction, isNil } from 'lodash'

const toFnRef = <T>(ref?: Ref<T>) =>
  isNil(ref) || isFunction(ref)
    ? ref
    : (value: T) => {
        ref.current = value
      }

export function mergeRefs<T>(refA?: Ref<T> | null, refB?: Ref<T> | null) {
  const a = toFnRef(refA)
  const b = toFnRef(refB)
  return (value: T) => {
    if (a) a(value)
    if (b) b(value)
  }
}

/**
 * Create and returns a single callback ref composed from two other Refs.
 *
 * ```tsx
 * const Button = React.forwardRef((props, ref) => {
 *   const [element, attachRef] = useCallbackRef<HTMLButtonElement>();
 *   const mergedRef = useMergedRefs(ref, attachRef);
 *
 *   return <button ref={mergedRef} {...props}/>
 * })
 * ```
 *
 * @param refA A Callback or mutable Ref
 * @param refB A Callback or mutable Ref
 * @category refs
 */
export function useMergedRef<T>(refA?: Ref<T>, refB?: Ref<T>) {
  return useMemo(() => mergeRefs(refA, refB), [refA, refB])
}
